"use client";

import React, { useState, useCallback, useEffect } from "react";
import { useTranslations } from "next-intl";
import { Link } from "@/i18n/navigation";
import { debounce } from "lodash";
import { FaCheck, FaTimes, FaSearch } from "react-icons/fa";
import { AuthorSuggestion } from "@/types/types";
import fetch from "@/components/fetch";

interface SearchHistoryItem {
  mtid: string;
  name: string;
  timestamp: number;
}

interface AuthorState {
  mtid: string;
  name: string;
  suggestions: AuthorSuggestion[];
  isValid: boolean | null;
}

const AuthorSearch: React.FC = () => {
  const t = useTranslations("authorSearch");
  const [authorState, setAuthorState] = useState<AuthorState>({
    mtid: "",
    name: "",
    suggestions: [],
    isValid: null,
  });
  const [searchHistory, setSearchHistory] = useState<SearchHistoryItem[]>([]);

  useEffect(() => {
    const savedHistory = localStorage.getItem("authorSearchHistory");
    if (savedHistory) {
      setSearchHistory(JSON.parse(savedHistory));
    }
  }, []);



  const fetchAuthorSuggestions = useCallback(
    debounce(async (input: string) => {
      if (input.length < 3) {
        setAuthorState((prev) => ({ ...prev, suggestions: [] }));
        return;
      }

      const response = await fetch.authorSuggestions(input);
      if (!response.ok) throw new Error(t("authorSuggestionsError"));
      setAuthorState((prev) => ({
        ...prev,
        suggestions: response.data.content.map((item: any) => ({
          id: item.mtid,
          name: item.label,
        })),
      }));
    }, 300),
    [t]
  );

  const checkMTMTID = useCallback(
    debounce(async (id: string) => {
      if (!id) {
        setAuthorState((prev) => ({ ...prev, isValid: null }));
        return;
      }

      const response = await fetch.author(id);
      setAuthorState((prev) => ({
        ...prev,
        isValid: response.ok,
        name: response.ok
          ? `${response.data.content.familyName} ${response.data.content.givenName} (${response.data.content.auxName})`
          : prev.name,
      }));
    }, 300),
    []
  );

  const handleAuthorNameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newAuthorName = e.target.value;
    setAuthorState((prev) => ({
      ...prev,
      name: newAuthorName,
      suggestions: [],
      isValid: null,
    }));
    fetchAuthorSuggestions(newAuthorName);
  };

  const handleMTMTIDChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newMTMTID = e.target.value;
    setAuthorState((prev) => ({
      ...prev,
      mtid: newMTMTID,
      suggestions: [],
    }));
    checkMTMTID(newMTMTID);
  };

  const handleAuthorSelect = (selectedAuthor: AuthorSuggestion) => {
    setAuthorState({
      name: selectedAuthor.name,
      mtid: selectedAuthor.id.toString(),
      suggestions: [],
      isValid: true,
    });
  };

  useEffect(() => {
    if (authorState.mtid) {
      checkMTMTID(authorState.mtid);
    }
  }, [authorState.mtid, checkMTMTID]);

  // Add this function to save search history
  const saveToSearchHistory = (mtid: string, name: string) => {
    const newHistoryItem: SearchHistoryItem = {
      mtid,
      name,
      timestamp: Date.now(),
    };

    const updatedHistory = [
      newHistoryItem,
      ...searchHistory.filter((item) => item.mtid !== mtid),
    ].slice(0, 3);

    setSearchHistory(updatedHistory);
    localStorage.setItem("authorSearchHistory", JSON.stringify(updatedHistory));
  };

  return (
    <div className="w-full">
      <div className="flex flex-col md:flex-row space-y-4 md:space-y-0 md:space-x-4">
        <div className="relative md:flex-[3]">
          <label
            htmlFor="authorName"
            className="block text-sm font-medium text-gray-700 mb-2"
          >
            {t("editTitle")}
          </label>
          <div className="relative">
            <input
              id="authorName"
              type="text"
              value={authorState.name}
              onChange={handleAuthorNameChange}
              placeholder={t("placeholder")}
              className="w-full h-12 text-base px-4 rounded-xl bg-white/90 border border-gray-300 focus:outline-none focus:border-bme-main transition duration-300"
            />
            {authorState.suggestions.length > 0 && (
              <ul className="absolute z-10 w-full bg-white border border-gray-200 rounded-xl mt-1 max-h-56 overflow-y-auto">
                {authorState.suggestions.map((author) => (
                  <li
                    key={author.id}
                    onClick={() => handleAuthorSelect(author)}
                    className="p-3 hover:bg-gray-50/90 cursor-pointer transition duration-200"
                  >
                    {author.name}
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>
        <div className="relative md:flex-1">
          <label
            htmlFor="mtmtId"
            className="block text-sm font-medium text-gray-700 mb-2"
          >
            {t("mtmtIdEditTitle")}
          </label>
          <div className="relative">
            <input
              id="mtmtId"
              type="text"
              value={authorState.mtid}
              onChange={handleMTMTIDChange}
              placeholder={t("mtmtIdPlaceholder")}
              maxLength={10}
              className="w-full h-12 text-base px-4 rounded-xl bg-white/90 border border-gray-300 focus:outline-none focus:border-bme-main transition duration-300 pr-10"
            />
            {authorState.isValid !== null && (
              <span className="absolute right-3 top-1/2 transform -translate-y-1/2">
                {authorState.isValid ? (
                  <FaCheck className="text-green-500" />
                ) : (
                  <FaTimes className="text-red-500" />
                )}
              </span>
            )}
          </div>
        </div>
      </div>

      {searchHistory.length > 0 && (
        <div className="mt-4 space-y-2">
          <p className="text-sm text-gray-600">{t("previousSearches")}</p>
          <div className="flex flex-wrap gap-2">
            {searchHistory.map((item) => (
              <Link
                key={item.mtid}
                href={`/${item.mtid}`}
                className="px-3 py-2 text-sm bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors duration-200 flex items-center space-x-2"
              >
                <span>{item.name}</span>
              </Link>
            ))}
          </div>
        </div>
      )}

      <div className="mt-6 flex justify-center">
        <Link
          href={`/${authorState.mtid}`}
          onClick={() => {
            if (authorState.mtid && authorState.name) {
              saveToSearchHistory(authorState.mtid, authorState.name);
            }
          }}
          className="inline-flex items-center justify-center gap-2 px-16 h-12 bg-bme-main text-white rounded-xl hover:bg-bme-main-hover focus:outline-none transition-colors duration-300 text-sm sm:text-base shadow-lg min-w-[350px]"
        >
          <FaSearch />
          <span>{t("search")}</span>
        </Link>
      </div>
    </div>
  );
};

export default AuthorSearch;
